import type { Throughput } from '@/features/project-board/analytics/throughput';

/**
 * 처리량 막대 그래프 — 차트 라이브러리 없이 SVG로 그린다(막대 12~14개에 의존성을 늘릴 이유가 없다).
 * 직전 4구간 평균선을 함께 그려 이번 구간이 평소보다 빠른지 느린지 바로 보이게 한다.
 *
 * 막대를 둘로 쌓는다 — 아래는 사람이 처리한 몫, 위는 AI-DART가 무인 처리한 이슈 몫. 총 높이(=`done`)는
 * 그대로 팀 처리량이면서, 그중 어디까지가 사람 손을 탔는지 한눈에 갈린다.
 */
export function ThroughputChart({ data }: { data: Throughput }): React.JSX.Element {
  const width = 660;
  const height = 170;
  const padding = { top: 12, right: 8, bottom: 22, left: 30 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const max = Math.max(1, ...data.buckets.map((bucket) => bucket.done));
  const barWidth = plotWidth / data.buckets.length;
  const y = (value: number) => padding.top + plotHeight - (value / max) * plotHeight;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-44 w-full" role="img" aria-label="처리량 추세">
      {[0, Math.round(max / 2), max].map((tick) => (
        <g key={tick}>
          <line x1={padding.left} x2={width - padding.right} y1={y(tick)} y2={y(tick)} className="stroke-line-soft" />
          <text x={padding.left - 6} y={y(tick) + 3} textAnchor="end" className="fill-faint text-[9px]">
            {tick}
          </text>
        </g>
      ))}

      {data.buckets.map((bucket, at) => {
        const isCurrent = at === data.buckets.length - 1;
        const { general } = bucket;
        const barX = padding.left + at * barWidth + barWidth * 0.18;
        const barW = barWidth * 0.64;
        return (
          <g key={bucket.start}>
            <rect
              x={barX}
              y={y(general)}
              width={barW}
              height={padding.top + plotHeight - y(general)}
              rx={2}
              className={isCurrent ? 'fill-accent' : 'fill-done/60'}
            >
              <title>{`${bucket.start} · 완료 ${bucket.done}건(사람 ${general}건 · AI-DART ${bucket.aiDart}건)`}</title>
            </rect>
            {bucket.aiDart > 0 && (
              <rect
                x={barX}
                y={y(bucket.done)}
                width={barW}
                height={y(general) - y(bucket.done)}
                rx={2}
                className="fill-progress/70"
              >
                <title>{`${bucket.start} · AI-DART 처리 ${bucket.aiDart}건`}</title>
              </rect>
            )}
            <text
              x={padding.left + at * barWidth + barWidth / 2}
              y={height - 7}
              textAnchor="middle"
              className={`text-[9px] ${isCurrent ? 'fill-accent font-bold' : 'fill-faint'}`}
            >
              {bucket.label}
            </text>
          </g>
        );
      })}

      <line
        x1={padding.left}
        x2={width - padding.right}
        y1={y(data.average4)}
        y2={y(data.average4)}
        className="stroke-progress"
        strokeDasharray="4 3"
      />
      <text x={width - padding.right} y={y(data.average4) - 4} textAnchor="end" className="fill-progress text-[9px]">
        직전 4구간 평균 {data.average4}
      </text>
    </svg>
  );
}
